// ─────────────────────────────────────────────────────────────
//  Экран «Тарифы»: что можно купить и сколько это стоит.
//
//  Цены НЕ считаются здесь. Бэк (GET /api/v1/tariffs) отдаёт их уже
//  посчитанными под регион пользователя: скидка тарифа и региональная наценка
//  применены, price — итог к оплате, full_price — зачёркнутая цена без скидки.
//  Экран только раскладывает готовые числа по карточкам, поэтому изменение
//  цены или процента в админке видно здесь сразу и без правок фронта.
//
//  Состав («что входит») тянется отдельным запросом по тапу: у подписки Таро
//  это все расклады каталога — держать их в списке тарифов было бы тяжело.
//
//  Оплата. По кнопке бэк выставляет счёт и отдаёт ссылку на форму Продамуса; она
//  открывается во ВНЕШНЕМ браузере (openExternalLink), потому что во встроенном
//  вебвью Telegram ломается 3-D Secure и возврат из приложения банка. Мини-апп при
//  этом не закрывается и остаётся ждать: подтверждает оплату не редирект, а вебхук
//  кассы, поэтому результат мы узнаём опросом GET /payments/{id} — он идёт, пока
//  вкладка мини-аппа снова не станет видимой и платёж не перейдёт в is_paid.
//
//  Исключение — пробный тариф (is_trial): он стоит 0 ₽, счёта в кассе на такую сумму
//  не бывает, поэтому у него своя кнопка «Забрать бесплатно». Она зовёт
//  POST /tariffs/trial/activate, доступ приходит прямо в ответе — ни браузера, ни
//  опроса. Забрать его можно один раз, поэтому кнопка гаснет по trial.activated,
//  который приходит сверху вместе с состоянием подарка.
// ─────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, Check, ChevronDown, ExternalLink, Gift, Send } from 'lucide-react'
import { ACCESS_CODES, activateTrialTariff, createPayment, fetchTariff, fetchTariffs, getPaymentStatus } from '../api.js'
import { hapticNotification, openChannelLink, openExternalLink } from '../tg.js'
import { Glass, money, Orb, Pill, SparkLine } from './ui.jsx'

const plural = (n, one, few, many) => {
  const m10 = n % 10, m100 = n % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
  return many
}
const nReadings = n => `${n} ${plural(n, 'разбор', 'разбора', 'разборов')}`
const nQuestions = n => `${n} ${plural(n, 'вопрос', 'вопроса', 'вопросов')}`
const nDays = n => `${n} ${plural(n, 'день', 'дня', 'дней')}`
const nSpreads = n => `${n} ${plural(n, 'расклад', 'расклада', 'раскладов')}`

// Группы витрины идут в этом порядке; тариф без своей группы (новый kind из
// админки) не теряется — он попадает в «Другое» в конце списка.
const GROUPS = [
  { kind: 'tarot_subscription', title: 'Подписка на Таро', copy: 'Расклады и вопросы в чате Clavis' },
  { kind: 'astro_package', title: 'Астро-разборы', copy: 'Блоки Формулы души — покупаешь пакет и выбираешь разборы' },
  { kind: 'message_pack', title: 'Дополнительно', copy: 'Докупить вопросы к уже открытому' },
]

/** Короткая подпись срока рядом с ценой: «/ мес» для месячной подписки. */
function periodLabel(tariff) {
  const days = tariff.duration_days
  if (!days) return ''
  if (days === 30) return '/ мес'
  if (days === 365) return '/ год'
  return `/ ${nDays(days)}`
}

/** Что входит в тариф — строки собираются из полей, а не из текста описания. */
function features(tariff) {
  const rows = []
  // `readings_quota` — одна колонка на оба модуля: у астро-пакета она считает разборы,
  // у пробного тарифа расклады. Слово выбирается по scope, иначе пробный обещал бы
  // разбор, которого не даёт. Вместе с «входит всё» квота не дублируется отдельной
  // строкой, а уточняет её: не «все расклады каталога», а «любой один из всего каталога».
  const spreadQuota = tariff.scope === 'tarot' ? tariff.readings_quota : null
  if (tariff.includes_all_spreads) {
    rows.push(spreadQuota ? `${nSpreads(spreadQuota)} на выбор из всего каталога` : 'Все расклады каталога')
  } else if (spreadQuota) {
    rows.push(`${nSpreads(spreadQuota)} на выбор`)
  }
  if (tariff.includes_all_readings) rows.push('Все разборы каталога')
  if (tariff.readings_quota && !spreadQuota) rows.push(`${nReadings(tariff.readings_quota)} на выбор`)
  if (tariff.total_messages) {
    const bonus = tariff.bonus_messages ? `, из них ${tariff.bonus_messages} бонусом` : ''
    rows.push(`${nQuestions(tariff.total_messages)} в чате с ИИ${bonus}`)
  }
  if (tariff.duration_days) rows.push(`Доступ на ${nDays(tariff.duration_days)}`)
  if (tariff.kind === 'tarot_subscription' && !tariff.is_trial && tariff.duration_days) {
    rows.push('Неиспользованные вопросы сгорают при окончании подписки')
  }
  // Послание дня сюда не попадает: оно бесплатно у всех и к тарифу отношения не имеет.
  if (tariff.readings_per_day) rows.push(`Не больше ${nReadings(tariff.readings_per_day)} в день`)
  if (tariff.messages_per_day) rows.push(`Не больше ${nQuestions(tariff.messages_per_day)} в день`)
  return rows
}

// Опрос статуса оплаты. Вебхук от кассы обычно доходит за секунды, но карта может
// уйти на 3-D Secure, а человек — задуматься над вводом кода, поэтому ждём долго и
// редко: лишние запросы здесь ничего не ускоряют.
const POLL_INTERVAL_MS = 4000
const POLL_TIMEOUT_MS = 10 * 60 * 1000

function TariffCard({ tariff, onBuy, onActivate, onOpenChannel, busy, awaiting, activated, needChannel }) {
  const [open, setOpen] = useState(false)
  const [months, setMonths] = useState(1)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const discount = Number(tariff.discount_percent) || 0
  const benefit = Number(tariff.compare_at_price) - Number(tariff.price)
  // Пробный тариф стоит 0 ₽: счёт в кассе на такую сумму не выставить, поэтому у него
  // своя кнопка — она сразу зовёт выдачу, без ссылки в браузер и без опроса статуса.
  const isTrial = !!tariff.is_trial
  const monthOption = tariff.month_options?.find((option) => option.months === months)
  const selectedPrice = monthOption?.price ?? tariff.price
  const selectedFullPrice = monthOption?.full_price ?? tariff.full_price

  // Состав грузим один раз и только когда его действительно раскрыли.
  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (!next || detail || loading) return
    setLoading(true)
    try { setDetail(await fetchTariff(tariff.id)) } catch { setDetail({ spreads: [], readings: [] }) }
    finally { setLoading(false) }
  }

  const items = [...(detail?.readings || []), ...(detail?.spreads || [])]
  const itemsLabel = detail
    ? [detail.readings?.length ? nReadings(detail.readings.length) : '', detail.spreads?.length ? nSpreads(detail.spreads.length) : '']
      .filter(Boolean).join(' · ')
    : ''

  return (
    <Glass className="tariff-card">
      <div className="tariff-head">
        <Orb />
        <div>
          <b>{tariff.title}</b>
          {tariff.subtitle && <small>{tariff.subtitle}</small>}
        </div>
        {isTrial && <i className="tariff-badge is-gift">Подарок</i>}
        {!isTrial && discount > 0 && <i className="tariff-badge">−{Math.round(discount)}%</i>}
      </div>

      <div className="tariff-price">
        <strong>{isTrial ? 'Бесплатно' : `${money(selectedPrice)} ₽`}</strong>
        <span>{months === 1 ? periodLabel(tariff) : `за ${months} мес.`}</span>
        {!isTrial && selectedFullPrice && <s>{money(selectedFullPrice)} ₽</s>}
      </div>
      {months === 1 && tariff.compare_at_price && benefit > 0 && (
        <p className="tariff-benefit">По отдельности {money(tariff.compare_at_price)} ₽ — выгода {money(benefit)} ₽</p>
      )}

      {tariff.description && <p className="tariff-copy">{tariff.description}</p>}

      <ul className="tariff-features">
        {features(tariff).map(row => <li key={row}>{row}</li>)}
      </ul>

      {tariff.month_options?.length > 1 && (
        <div className="tariff-months">
          <label htmlFor={`months-${tariff.id}`}>Срок подписки</label>
          <select id={`months-${tariff.id}`} value={months} onChange={(event) => setMonths(Number(event.target.value))} disabled={busy || awaiting}>
            {tariff.month_options.map((option) => (
              <option key={option.months} value={option.months}>
                {option.months} {plural(option.months, 'месяц', 'месяца', 'месяцев')} · {money(option.price)} ₽{Number(option.month_discount_percent) > 0 ? ` · −${Number(option.month_discount_percent)}%` : ''}
              </option>
            ))}
          </select>
          <small>Доступ на {nDays(monthOption?.duration_days ?? tariff.duration_days)}. Вопросы и лимиты начисляются за весь срок.</small>
        </div>
      )}

      {isTrial ? (
        <>
          <button
            type="button"
            className="tariff-buy press"
            onClick={() => onActivate(tariff)}
            disabled={busy || activated}
          >
            {busy && 'Активируем…'}
            {!busy && activated && <>Уже активирован <Check size={15} /></>}
            {!busy && !activated && <>Забрать бесплатно <Gift size={15} /></>}
          </button>
          {/* Условие показываем только после отказа, а не заранее: до нажатия мы не
              знаем, подписан человек или нет, и подписчику это была бы лишняя преграда
              перед кнопкой. Блок остаётся на экране под кнопкой — вернувшись из канала,
              человек жмёт «Забрать» ещё раз, и она прямо над ним. */}
          {needChannel && (
            <div className="tariff-channel" role="status">
              <p><b>Подпишись на канал</b><span>Бесплатный расклад — для подписчиков. Подпишись и нажми «Забрать» ещё раз.</span></p>
              {needChannel.url && (
                <button type="button" className="press" onClick={() => onOpenChannel(needChannel.url)}>
                  Перейти {needChannel.title || ''} <Send size={14} />
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <button
          type="button"
          className="tariff-buy press"
          onClick={() => onBuy(tariff, months)}
          disabled={busy || awaiting}
        >
          {busy && 'Готовим оплату…'}
          {!busy && awaiting && 'Ждём подтверждения…'}
          {!busy && !awaiting && <>Оплатить {money(selectedPrice)} ₽ <ExternalLink size={15} /></>}
        </button>
      )}
      {awaiting && (
        <p className="tariff-await">
          Форма оплаты открылась в браузере. Оплати и возвращайся сюда — доступ откроется сам.
        </p>
      )}

      <button type="button" className={`tariff-more press ${open ? 'is-open' : ''}`} onClick={toggle} aria-expanded={open}>
        Что входит {itemsLabel && <em>{itemsLabel}</em>} <ChevronDown size={16} />
      </button>
      {open && (
        <div className="tariff-contents">
          {loading && <p className="result-placeholder">✨ Загружаем состав…</p>}
          {!loading && !items.length && <p className="result-placeholder">Состав уточняется.</p>}
          {items.map(item => (
            <span key={`${item.section || item.sphere}-${item.id}`}>
              <b>{item.title}</b>
              <small>{item.section || item.sphere}</small>
            </span>
          ))}
        </div>
      )}
    </Glass>
  )
}

export default function TariffsScreen({ go, back, onAccessChanged, trial, onTrialChanged }) {
  const [tariffs, setTariffs] = useState(null)
  const [error, setError] = useState('')
  // Счёт, который сейчас оплачивают в браузере: {paymentId, tariffId, title}.
  const [pending, setPending] = useState(null)
  const [paid, setPaid] = useState('')
  const [busyId, setBusyId] = useState(null)
  // Канал, на который надо подписаться. Ставится только после отказа бэка и держится
  // до удачной активации: блок должен остаться на месте, пока человек ходит подписываться.
  const [needChannel, setNeedChannel] = useState(null)
  // Колбэк обновления доступа держим в ref: иначе новая функция от родителя на
  // каждый рендер перезапускала бы опрос и он бы никогда не досчитал свой таймаут.
  const accessChanged = useRef(onAccessChanged)
  accessChanged.current = onAccessChanged
  const trialChanged = useRef(onTrialChanged)
  trialChanged.current = onTrialChanged

  useEffect(() => {
    let live = true
    fetchTariffs()
      .then(rows => { if (live) setTariffs(Array.isArray(rows) ? rows : []) })
      .catch(() => {
        if (!live) return
        setTariffs([])
        setError('Не удалось загрузить тарифы. Проверь соединение и попробуй ещё раз.')
      })
    return () => { live = false }
  }, [])

  // Выставить счёт и уйти платить. Мини-апп при этом НЕ закрывается: оплата идёт в
  // соседнем окне, а мы остаёмся ждать вебхук.
  //
  // Ссылка открывается уже после ответа бэка, то есть вне обработчика тапа. Внутри
  // Telegram это ничего не меняет (openLink не считается всплывающим окном), а вот
  // запасной window.open в обычном браузере может поймать блокировщик — там ссылку
  // придётся открыть вручную. Целевая среда у нас Telegram, так что живём с этим.
  const buy = async (tariff, months = 1) => {
    setBusyId(tariff.id)
    setError('')
    setPaid('')
    try {
      const order = await createPayment(tariff.id, months)
      openExternalLink(order.url)
      setPending({ paymentId: order.payment_id, tariffId: tariff.id, title: tariff.title, months })
    } catch (err) {
      setError(err?.status === 503
        ? 'Оплата временно недоступна — касса не отвечает. Попробуй чуть позже.'
        : 'Не удалось создать счёт. Попробуй ещё раз.')
    } finally {
      setBusyId(null)
    }
  }

  // Забрать бесплатный тариф. Ни ссылки в браузер, ни опроса статуса: платить нечего,
  // бэк выдаёт доступ прямо в ответе, поэтому дальше остаётся только перечитать доступ
  // и статус подарка — по нему кнопка станет «Уже активирован», а всплывашка исчезнет.
  const activate = async tariff => {
    setBusyId(tariff.id)
    setError('')
    setPaid('')
    try {
      await activateTrialTariff()
      hapticNotification('success')
      setPaid(`Тариф «${tariff.title}» активирован. Бесплатный расклад уже доступен.`)
      // Условие выполнено — убираем напоминание о подписке, если оно висело.
      setNeedChannel(null)
      accessChanged.current?.()
      trialChanged.current?.()
    } catch (err) {
      // 402 + код канала — не подписан. Единственный отказ, который человек снимает
      // сам и не выходя из этого экрана: показываем блок с кнопкой в канал и ждём
      // повторного нажатия.
      if (err?.status === 402 && err?.code === ACCESS_CODES.CHANNEL) {
        hapticNotification('warning')
        // Канал приезжает в теле отказа; статус подарка — запасной источник на случай,
        // если бэк почему-то его не приложил.
        setNeedChannel(err?.body?.channel || trial?.channel || {})
      } else if (err?.status === 409) {
        // Уже забирал (соседняя вкладка, двойной тап): доступ у человека есть,
        // ошибка ему ни о чём не скажет, поэтому просто подтягиваем актуальный статус.
        trialChanged.current?.()
        setPaid('Бесплатный тариф уже активирован.')
      } else if (err?.status === 503) {
        // Подписку не удалось проверить — это наша поломка, а не отказ человеку.
        setError('Не получилось проверить подписку. Попробуй ещё раз через минуту.')
      } else {
        setError(err?.status === 404
          ? 'Бесплатный тариф больше не доступен.'
          : 'Не удалось активировать тариф. Попробуй ещё раз.')
      }
    } finally {
      setBusyId(null)
    }
  }

  // Уводим в канал. Как именно — решает openChannelLink: способ зависит от клиента,
  // см. tg.js. Подписку мы у себя не запоминаем и статус не перезапрашиваем: проверит
  // её бэк, когда человек вернётся и нажмёт «Забрать» второй раз.
  const openChannel = url => openChannelLink(url)

  // Ждём подтверждения: оплату подтверждает вебхук кассы, а не возврат в приложение,
  // поэтому узнать результат можно только спросив бэк.
  useEffect(() => {
    if (!pending) return undefined
    let live = true
    const until = Date.now() + POLL_TIMEOUT_MS

    const check = async () => {
      if (!live) return
      try {
        const status = await getPaymentStatus(pending.paymentId)
        if (!live) return
        if (status?.is_paid) {
          setPending(null)
          setPaid(`Оплата прошла. Доступ по тарифу «${status.tariff_title || pending.title}» на ${status.months || pending.months || 1} мес. уже открыт.`)
          hapticNotification('success')
          // Замки в каталоге и счётчик вопросов живут на уровне приложения — просим
          // его перечитать доступ, иначе они разъедутся с реальностью до перезахода.
          accessChanged.current?.()
          return
        }
      } catch {
        // Сеть моргнула или бэк ответил ошибкой — не страшно, есть следующий тик.
      }
      // Ждать бесконечно незачем: человек мог просто закрыть форму, не заплатив.
      if (live && Date.now() > until) setPending(null)
    }

    const timer = setInterval(check, POLL_INTERVAL_MS)
    // Возврат из браузера — самый вероятный момент, когда деньги уже прошли.
    // Спрашиваем сразу, чтобы не показывать «ждём» лишние секунды.
    const onVisible = () => { if (!document.hidden) check() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [pending])

  const known = GROUPS.map(g => g.kind)
  const groups = [
    ...GROUPS.map(group => ({ ...group, items: (tariffs || []).filter(t => t.kind === group.kind) })),
    { kind: 'other', title: 'Другое', copy: '', items: (tariffs || []).filter(t => !known.includes(t.kind)) },
  ].filter(group => group.items.length > 0)

  return (
    <section className="screen tariffs-screen">
      <header className="topbar">
        <button className="press" onClick={() => go(back)}><ArrowLeft size={17} /> Назад</button>
        <span><Orb /> Тарифы</span>
      </header>
      <h2 className="page-title">Тарифы</h2>
      <p className="subtitle">Подписка на расклады, пакеты разборов и вопросы к ИИ</p>
      <SparkLine />

      {error && <p className="api-error" role="alert">{error}</p>}
      {paid && <p className="tariff-paid" role="status">{paid}</p>}
      {tariffs === null && !error && <p className="result-placeholder">✨ Загружаем тарифы…</p>}
      {tariffs !== null && !groups.length && !error && <p className="result-placeholder">Тарифы пока не настроены.</p>}

      {groups.map(group => (
        <div className="tariff-group" key={group.kind}>
          <h3>{group.title}</h3>
          {group.copy && <p className="subtitle">{group.copy}</p>}
          {group.items.map(tariff => (
            <TariffCard
              key={tariff.id}
              tariff={tariff}
              onBuy={buy}
              onActivate={activate}
              onOpenChannel={openChannel}
              needChannel={tariff.is_trial ? needChannel : null}
              busy={busyId === tariff.id}
              awaiting={pending?.tariffId === tariff.id}
              activated={!!tariff.is_trial && !!trial?.activated}
            />
          ))}
        </div>
      ))}

      <Glass className="question-card">
        <div><b>Остались вопросы по тарифам?</b><span>Спроси меня в чате — подскажу, что подойдёт</span></div>
        <Pill onClick={() => go('chat')}>Написать</Pill>
      </Glass>
    </section>
  )
}
